import type {
  CreativeAdComposition,
  CreativeItem,
} from "@digital-twins/shared-types";
import type { AdRenderSources } from "@/types/creative-layouts";
import api from "@/lib/api/client";
import { creativeLibraryApi } from "@/lib/api/creative-library";

export class AdSourceError extends Error {
  constructor(public readonly code: "stale" | "source") {
    super(code);
  }
}
export function assertAdSource(
  item: CreativeItem,
  expected: { token: string; revision: number },
) {
  if (item.token !== expected.token || item.revision !== expected.revision)
    throw new AdSourceError("stale");
}
/**
 * Reauthorize every export; a cached preview blob does not confer current source access.
 * Allowed use warns in the editor and never blocks here.
 */
export async function readAdSources(
  composition: CreativeAdComposition,
): Promise<AdRenderSources> {
  const source = await creativeLibraryApi.creative(composition.source.token);
  assertAdSource(source, composition.source);
  if (composition.logo) {
    const logo = await creativeLibraryApi.creative(composition.logo.token);
    assertAdSource(logo, composition.logo);
    if (logo.role !== "logo") throw new AdSourceError("source");
  }
  const read = async (token: string) =>
    (
      await api.get<Blob>(`/library/creatives/${token}/content`, {
        responseType: "blob",
      })
    ).data;
  const bytes = {
    image: await read(source.token),
    logo: composition.logo ? await read(composition.logo.token) : null,
  };
  assertAdSource(
    await creativeLibraryApi.creative(source.token),
    composition.source,
  );
  if (composition.logo)
    assertAdSource(
      await creativeLibraryApi.creative(composition.logo.token),
      composition.logo,
    );
  return bytes;
}
export function downloadAdBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
